import {
  Badge,
  CalendarDaysIcon,
  ClockIcon,
  DocumentTextIcon,
  FolderIcon,
  formatTimeDate,
  LinkIcon,
  RocketLaunchIcon,
  SwatchIcon,
} from "@";
import type { ElementType } from "react";
import {
  cardFooter,
  cardSkeletonBlock,
  cardSkeletonSurface,
  cardSurface,
} from "../../shared/cards";
import type { LandingPage } from "../types";
import { TableActions } from "./TableOptions";

// Small label/value pair used in the card's meta footer.
const Meta = ({
  icon: Icon,
  label,
  value,
}: {
  icon: ElementType;
  label: string;
  value?: string | null;
}) => (
  <span className="inline-flex items-center gap-1.5">
    <Icon className="size-3.5 shrink-0 opacity-60" aria-hidden />
    <span>{label}</span>
    <span className="font-medium tabular-nums text-gray-600 dark:text-gray-300">
      {value ? formatTimeDate(value) : "—"}
    </span>
  </span>
);

// Project / theme chip — filled when present, dashed-outline when not.
const Chip = ({
  icon: Icon,
  label,
  fallback,
}: {
  icon: ElementType;
  label?: string;
  fallback: string;
}) => (
  <span
    className={`inline-flex max-w-full items-center gap-1.5 rounded-lg border px-2 py-1 text-[11px] font-medium ${
      label
        ? "border-gray-100 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-700/40 dark:text-gray-300"
        : "border-dashed border-gray-200 text-gray-400 dark:border-gray-700 dark:text-gray-500"
    }`}
  >
    <Icon className="size-3.5 shrink-0 opacity-60" aria-hidden />
    <span className="truncate">{label || fallback}</span>
  </span>
);

interface Props {
  page: LandingPage;
  deleteHandler: (id: number) => Promise<void>;
  togglePublish: (id: number, value: boolean) => Promise<void>;
}

export const LandingPageCard = ({
  page,
  deleteHandler,
  togglePublish,
}: Props) => {
  const published = !!page.isPublished;

  return (
    <article className={cardSurface}>
      <div className="flex flex-1 flex-col gap-3 p-5">
        {/* Header — icon, name + slug, status pill */}
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-purple-50 text-purple-700 ring-1 ring-purple-100 dark:bg-purple-900/30 dark:text-purple-300 dark:ring-purple-700/40">
            <DocumentTextIcon className="h-5 w-5" />
          </span>

          <div className="min-w-0 flex-1">
            <h3 className="line-clamp-2 text-sm font-bold leading-snug tracking-tight text-gray-900 dark:text-gray-100">
              {page.name || "—"}
            </h3>
            {page.slug && (
              <span
                dir="ltr"
                className="mt-1 inline-flex max-w-full items-center gap-1 truncate font-mono text-[11px] text-gray-400 dark:text-gray-500"
              >
                <LinkIcon className="size-3 shrink-0" aria-hidden />
                /lp/{page.slug}
              </span>
            )}
          </div>

          <span className="inline-flex shrink-0 items-center">
            <Badge
              color={published ? "green" : "red"}
              title={published ? "منشورة" : "مسودة"}
            />
          </span>
        </div>

        {/* Project & theme */}
        <div className="flex flex-wrap gap-2">
          <Chip
            icon={FolderIcon}
            label={page.projectTitle}
            fallback="بدون مشروع"
          />
          <Chip icon={SwatchIcon} label={page.themeTitle} fallback="بدون ثيم" />
        </div>

        {/* Dates */}
        <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1.5 pt-1 text-[11px] text-gray-400 dark:text-gray-500">
          <Meta icon={CalendarDaysIcon} label="أُنشئت" value={page.createdAt} />
          {published && page.publishedAt && (
            <Meta
              icon={RocketLaunchIcon}
              label="نُشرت"
              value={page.publishedAt}
            />
          )}
          <Meta icon={ClockIcon} label="آخر تحديث" value={page.updatedAt} />
        </div>
      </div>

      {/* Actions */}
      <div className={cardFooter}>
        <TableActions
          row={page}
          deleteHandler={deleteHandler}
          TogglePublished={togglePublish}
        />
      </div>
    </article>
  );
};

export const LandingPageCardSkeleton = () => (
  <div className={cardSkeletonSurface}>
    <div className="flex flex-col gap-4 p-5">
      <div className="flex items-start gap-3">
        <span className={`h-9 w-9 shrink-0 ${cardSkeletonBlock} rounded-xl`} />
        <div className="flex-1 space-y-2">
          <span className={`block h-4 w-3/4 ${cardSkeletonBlock}`} />
          <span className={`block h-3 w-1/2 ${cardSkeletonBlock}`} />
        </div>
        <span className={`h-5 w-16 shrink-0 ${cardSkeletonBlock} rounded-full`} />
      </div>
      <div className="flex gap-2">
        <span className={`h-6 w-24 ${cardSkeletonBlock} rounded-lg`} />
        <span className={`h-6 w-20 ${cardSkeletonBlock} rounded-lg`} />
      </div>
      <span className={`h-3 w-full ${cardSkeletonBlock}`} />
    </div>
    <div className="border-t border-gray-100 px-3 py-3 dark:border-gray-700/60">
      <span className={`block h-9 w-44 ${cardSkeletonBlock} rounded-2xl`} />
    </div>
  </div>
);
